import clsx from 'clsx';
import React, { useRef, useState } from 'react';
import AuthCode, { AuthCodeRef } from 'react-auth-code-input';

import { ButtonWithLoadingState } from 'components/curo-components/common/Button';
import { useLoadingState } from 'hooks/useLoadingState';
import TimerMessage from 'product_modules/components/TimerMessage';
import useTimer from 'product_modules/hooks/useTimer';
import { ReloadIcon } from 'static/images';
import styled from 'types/AppTheme';

import styles from './AuthCodeInput.module.scss';

const CODE_LENGTH = 6;
const RESEND_RATE = 60;

interface IAuthCodeInputProps {
  handleVerifyCode: (value: string) => Promise<void>;
  handleResendCode: () => Promise<void>;
  buttonTitle: string;
  sendingAttemptsLeft?: number;
}

const StyledResendLink = styled.p`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: ${({ theme }) => theme.colors.accentColor};
  gap: 8px;
  cursor: pointer;
`;

const AuthCodeInput = ({
  handleVerifyCode,
  buttonTitle,
  handleResendCode,
  sendingAttemptsLeft,
}: IAuthCodeInputProps) => {
  const authCodeRef = useRef<AuthCodeRef | null>(null);
  const [code, setCode] = useState<string>('');
  const [verifyCodeLoadingStatus, setVerifyCodeLoadingStatus] = useLoadingState();
  const [resendCodeLoadingStatus, setResendCodeLoadingStatus] = useLoadingState();

  const { isRunning, restart, seconds } = useTimer(RESEND_RATE, {
    autoStart: true,
  });

  const handleChange = async (value: string) => {
    setVerifyCodeLoadingStatus('idle');
    setCode(value);
  };

  const handleButtonClick = async () => {
    if (code.length === CODE_LENGTH) {
      try {
        setVerifyCodeLoadingStatus('loading');
        await handleVerifyCode(code);
      } catch {
        if (authCodeRef.current) {
          authCodeRef.current.clear();
        }

        setVerifyCodeLoadingStatus('error');
      }
    } else {
      setVerifyCodeLoadingStatus('idle');
    }
  };

  const resendCode = async () => {
    try {
      setResendCodeLoadingStatus('loading');
      await handleResendCode();
      if (authCodeRef.current) {
        authCodeRef.current.clear();
      }
      restart();
      setResendCodeLoadingStatus('success');
    } catch {
      setResendCodeLoadingStatus('error');
    }
  };

  const renderResendLink = () => {
    if (sendingAttemptsLeft ? isRunning && sendingAttemptsLeft > 0 : isRunning) {
      return (
        <TimerMessage>
          Get a New Code in <strong>{seconds || 0} s</strong>
        </TimerMessage>
      );
    }

    return (
      <button
        onClick={resendCode}
        className={styles.resendCode}
        disabled={resendCodeLoadingStatus === 'loading' || sendingAttemptsLeft === 0}
      >
        <StyledResendLink>
          <ReloadIcon />
          Click here to resend code
        </StyledResendLink>
      </button>
    );
  };

  const renderVerifyCodeErrorMessage = () => {
    return (
      verifyCodeLoadingStatus === 'error' && (
        <p className={styles.errorMessage}>
          The code you provided did not match our records. Please try again.
        </p>
      )
    );
  };

  const isButtonDisabled = code.length !== CODE_LENGTH;

  return (
    <div className={styles.wrapper}>
      <div className={styles.inputWrapper}>
        <AuthCode
          length={CODE_LENGTH}
          containerClassName={styles.inputWrapper}
          inputClassName={clsx(
            styles.inputItem,
            verifyCodeLoadingStatus === 'error' && styles.errorInput,
          )}
          allowedCharacters="numeric"
          onChange={handleChange}
          disabled={verifyCodeLoadingStatus === 'loading'}
          ref={authCodeRef}
        />
      </div>
      {renderVerifyCodeErrorMessage()}
      {renderResendLink()}
      <ButtonWithLoadingState
        kind="primary"
        size="form"
        onClick={handleButtonClick}
        disabled={isButtonDisabled}
      >
        {buttonTitle}
      </ButtonWithLoadingState>
    </div>
  );
};

export default AuthCodeInput;
